// Emailing POs from Gmail (ERP-PLAN §5.5), shared by the browser (the compose dialog fills in at once from
// synced rows) and the server (it checks what the browser sends, and links vendor replies): who a PO goes to,
// the subject and body templates (English, or Chinese for vendor.language "zh"), who it's sent from, and
// which messages on a thread count as the vendor's.
import { z } from "zod";
import { hash53 } from "./hash.ts";
import { planCheckSeen } from "./mutations-po-plan-check.ts";

/** Stands in for the vendor documents link in the body; the server fills in the PO's link when it sends. */
export const DOCS_PLACEHOLDER = "[documents link]";

export const GMAIL_SEND_SCOPE = "https://www.googleapis.com/auth/gmail.send";
export const GMAIL_READ_SCOPES = {
  readonly: "https://www.googleapis.com/auth/gmail.readonly",
  metadata: "https://www.googleapis.com/auth/gmail.metadata",
} as const;

/** Whether this server can send POs from Gmail, and if not, what an admin has to set (from /api/me). */
export type GmailFeature = { enabled: boolean; fake: boolean; missing: string[] };

export type EmailLanguage = "en" | "zh";
export const emailLanguage = (vendorLanguage: string): EmailLanguage => (/^zh/i.test(vendorLanguage.trim()) ? "zh" : "en");

const EMAIL = z.email();
export const isEmail = (s: string) => EMAIL.safeParse(s).success;

/** "a@x.com, B <b@y.com>; c@z.com" → ["a@x.com", "b@y.com", "c@z.com"] (lower case, no repeats); bad ones apart. */
export function parseAddresses(text: string): { emails: string[]; bad: string[] } {
  const emails: string[] = [];
  const bad: string[] = [];
  for (const part of text.split(/[,;\n]+/)) {
    const raw = part.trim();
    if (!raw) continue;
    const email = (raw.match(/<([^>]+)>/)?.[1] ?? raw).trim().toLowerCase();
    if (!isEmail(email)) bad.push(raw);
    else if (!emails.includes(email)) emails.push(email);
  }
  return { emails, bad };
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? "";
const join = (names: string[]) => (names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`);

/** "PO-1001", or "PO-1001 rev 2" once it has revisions. */
export const poLabel = (po: { number: string; revision: number }) => (po.revision > 0 ? `${po.number} rev ${po.revision}` : po.number);

/** The PDF's file name, as GET /api/po/:id/pdf names it. */
export const pdfFileName = (po: { number: string; revision: number }, vendorName: string) =>
  `${`${poLabel(po)} ${vendorName}`.replace(/[^\w .,-]+/g, "").replace(/[. ]+$/, "")}.pdf`;

export type ComposeInput = {
  po: { number: string; revision: number; sent_revision: number | null };
  vendor: { name: string; language: string };
  contacts: { name: string; email: string; is_po_recipient: boolean }[];
  /** Who it's from: the sign-off uses their first name. */
  sender: { name: string; email: string } | null;
};

export type Compose = { to: string[]; cc: string[]; subject: string; body: string; language: EmailLanguage; attachment: string };

/** What the compose dialog starts with. Everything stays editable. */
export function composeDefaults({ po, vendor, contacts, sender }: ComposeInput): Compose {
  const language = emailLanguage(vendor.language);
  const recipients = contacts.filter((c) => c.is_po_recipient && isEmail(c.email.trim().toLowerCase()));
  const to = [...new Set(recipients.map((c) => c.email.trim().toLowerCase()))];
  const names = [...new Set(recipients.map((c) => firstName(c.name)).filter(Boolean))];
  const update = po.sent_revision !== null; // the vendor already has an earlier copy
  const who = firstName(sender?.name ?? "") || (sender?.email.split("@")[0] ?? "");
  const label = poLabel(po);
  if (language === "zh") {
    const rev = po.revision > 0 ? `（第 ${po.revision} 版）` : "";
    return {
      to,
      cc: [],
      language,
      attachment: pdfFileName(po, vendor.name),
      subject: `采购订单 ${po.number}${rev} - Hand Held Legend`,
      body: [
        `${names.length ? `${names.join("、")}，` : ""}您好！`,
        "",
        update ? `附件是采购订单 ${po.number} 的第 ${po.revision} 版，请以此版本为准。` : `附件是采购订单 ${po.number}${rev}，请查收。`,
        "",
        `本订单的生产资料（规格、图纸和文件）：${DOCS_PLACEHOLDER}`,
        "",
        "请确认数量、价格和交货日期。",
        "",
        "谢谢！",
        who,
        "Hand Held Legend",
      ].join("\n"),
    };
  }
  return {
    to,
    cc: [],
    language,
    attachment: pdfFileName(po, vendor.name),
    subject: `Purchase order ${label} from Hand Held Legend`,
    body: [
      names.length ? `Hi ${join(names)},` : "Hello,",
      "",
      update
        ? `Attached is revision ${po.revision} of purchase order ${po.number}. It replaces the copy we sent before.`
        : `Please find attached purchase order ${label}.`,
      "",
      `Production documents (specs, drawings and files) for this order: ${DOCS_PLACEHOLDER}`,
      "",
      "Please confirm the quantities, prices and ship dates.",
      "",
      "Thank you,",
      who,
      "Hand Held Legend",
    ].join("\n"),
  };
}

/** The body as sent: the placeholder becomes the documents link, or its line goes when there's no live link. */
export function fillBody(body: string, docsUrl: string | null): string {
  if (docsUrl) return body.replaceAll(DOCS_PLACEHOLDER, docsUrl);
  return body
    .split("\n")
    .filter((line) => !line.includes(DOCS_PLACEHOLDER))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n");
}

// What the vendor would get --------------------------------------------------------------------------

/** Everything on the PDF that comes from the PO and its lines (status, sent dates and bookkeeping aside). */
const STAMP_PO = [
  "revision",
  "kind",
  "vendor_id",
  "ship_to_location_id",
  "currency",
  "payment_terms",
  "incoterms",
  "deposit_pct",
  "expected_ship",
  "expected_arrive",
  "vendor_ref",
  "notes",
] as const;
const STAMP_LINE = [
  "id",
  "position",
  "item_id",
  "sku",
  "name",
  "vendor_sku",
  "vendor_name",
  "photo_url",
  "qty",
  "unit_price",
  "promised_date",
  "notes",
  "spec_revision_id",
] as const;
const norm = (v: unknown) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number(v.toPrecision(15));
  const s = String(v);
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(Number(s).toPrecision(15));
  if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(s)) return s.slice(0, 10);
  return s;
};

/**
 * A fingerprint of what the vendor would get: the PO's printed fields and every line, from rows as browsers have
 * them (synced) or as the server reads them with to_jsonb(). The send dialog takes one when it opens, and the
 * server refuses to send (or, mid-send, to mark as sent) when the PO no longer matches it, even for edits to a
 * draft, which don't move the revision.
 */
export function poStamp(po: object, lines: readonly object[]): string {
  const row = (o: object) => o as Record<string, unknown>;
  const data = JSON.stringify([
    STAMP_PO.map((k) => norm(row(po)[k])),
    [...lines]
      .map(row)
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
      .map((l) => STAMP_LINE.map((k) => norm(l[k]))),
  ]);
  return hash53(data);
}

// Senders -------------------------------------------------------------------------------------------

export type Mailbox = { id: string; user_id: string; email: string; name: string; shared: boolean; status: string };

/** A mailbox this person may send from: their own, or one its owner shared, and still connected. */
export const canUseMailbox = (box: Mailbox, userId: string) => box.status === "connected" && (box.user_id === userId || box.shared);

export type SenderChoice = {
  /** The address POs to this vendor go from ('' = the person sending, who has no Gmail connected). */
  email: string;
  why: "vendor" | "default" | "self";
  /** The connected mailbox for it this person may use, if any. */
  mailbox: Mailbox | null;
  /** Why it can't be used as it is (shown in the dialog), or '' when it can. */
  problem: string;
  /** Every mailbox this person may send from, the chosen one first. */
  options: Mailbox[];
};

/**
 * Who a PO is sent from: the vendor's own choice, else the default for everyone, else the person sending
 * (their own connected Gmail). The person sending can still pick another mailbox they may use.
 */
export function chooseSender({
  vendorSender,
  defaultSender,
  user,
  mailboxes,
}: {
  vendorSender: string;
  defaultSender: string;
  user: { id: string; email: string };
  mailboxes: Mailbox[];
}): SenderChoice {
  const usable = mailboxes.filter((m) => canUseMailbox(m, user.id));
  const own = usable.filter((m) => m.user_id === user.id);
  const mine = own.find((m) => m.email.toLowerCase() === user.email.toLowerCase()) ?? own[0] ?? null;
  const wanted = vendorSender.trim().toLowerCase() || defaultSender.trim().toLowerCase();
  const why = vendorSender.trim() ? "vendor" : defaultSender.trim() ? "default" : "self";
  const order = (first: Mailbox | null) => (first ? [first, ...usable.filter((m) => m.id !== first.id)] : usable);
  if (!wanted) {
    return { email: mine?.email ?? "", why, mailbox: mine, problem: mine ? "" : "Connect your Gmail in Settings → Email to send POs.", options: order(mine) };
  }
  const box = mailboxes.find((m) => m.email.toLowerCase() === wanted) ?? null;
  const ok = box && canUseMailbox(box, user.id) ? box : null;
  let problem = "";
  if (!box) problem = `POs ${why === "vendor" ? "to this vendor " : ""}go from ${wanted}, which hasn't connected its Gmail yet (Settings → Email).`;
  else if (box.status !== "connected") problem = `${wanted} needs to reconnect its Gmail (Settings → Email).`;
  else if (!ok) problem = `${wanted} isn't shared, so only the person who connected it can send from it.`;
  return { email: wanted, why, mailbox: ok, problem, options: order(ok) };
}

// Replies -------------------------------------------------------------------------------------------

/**
 * Free mail: an address there says nothing about which company it belongs to, so its domain never makes a sender
 * the vendor. Subdomains count too (vip.qq.com, vip.163.com), and so do the big brands' country domains
 * (yahoo.co.jp, hotmail.co.uk, live.cn).
 */
const FREE_MAIL = new Set([
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "yahoo.com",
  "ymail.com",
  "rocketmail.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "yandex.ru",
  "mail.ru",
  "qq.com",
  "foxmail.com",
  "163.com",
  "126.com",
  "yeah.net",
  "188.com",
  "sina.com",
  "sina.cn",
  "sohu.com",
  "aliyun.com",
  "139.com",
  "189.cn",
  "wo.cn",
  "tom.com",
  "21cn.com",
  "naver.com",
  "daum.net",
]);
/** yahoo.co.jp, hotmail.co.uk, live.cn, outlook.de…: the first label is a free-mail brand. */
const FREE_MAIL_BRANDS = new Set(["yahoo", "hotmail", "live", "outlook", "msn", "gmx", "yandex", "aol"]);
export function isFreeMail(domain: string): boolean {
  const d = domain.toLowerCase().replace(/\.$/, ""); // a trailing dot (fully qualified) changes nothing
  if (!d) return true;
  for (let rest = d; rest.includes("."); rest = rest.slice(rest.indexOf(".") + 1)) if (FREE_MAIL.has(rest)) return true;
  return FREE_MAIL_BRANDS.has(d.split(".")[0] ?? "");
}
const domainOf = (email: string) => email.split("@")[1]?.toLowerCase() ?? "";

/** "Lily Chen <lily@tailhoo.com>" → { name, email }. */
export function parseFrom(header: string): { name: string; email: string } {
  const m = header.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/);
  if (m) return { name: m[1].trim(), email: m[2].trim().toLowerCase() };
  return { name: "", email: header.trim().toLowerCase() };
}

/**
 * Whether a message on a PO's thread is the vendor writing, so it may be stored: from exactly one of the vendor's
 * contacts or an address the PO was sent To, or from the company domain of one of the vendor's own contacts (never
 * a free-mail domain, never a domain only a To or Cc address has). So a Cc'd customer, a forwarder and their
 * colleagues, and anything from HHL (the sender's mailbox, staff, HHL's domain) are never the vendor.
 */
export function isVendorMessage(from: string, { contacts, to, ourEmails }: { contacts: string[]; to: string[]; ourEmails: string[] }): boolean {
  const email = from.toLowerCase();
  if (!isEmail(email)) return false;
  const ours = new Set(ourEmails.map((e) => e.toLowerCase()));
  const ourDomains = new Set([...ours].map(domainOf).filter((d) => d && !isFreeMail(d)));
  const domain = domainOf(email);
  if (ours.has(email) || ourDomains.has(domain)) return false;
  const exact = new Set([...contacts, ...to].map((e) => e.toLowerCase()));
  if (exact.has(email)) return true;
  if (isFreeMail(domain)) return false;
  return contacts.some((c) => domainOf(c) === domain);
}

/** Opens a thread in Gmail on the web, in the mailbox it's in. */
export const gmailThreadUrl = (mailbox: string, threadId: string) =>
  `https://mail.google.com/mail/?authuser=${encodeURIComponent(mailbox)}#all/${encodeURIComponent(threadId)}`;

// Input -----------------------------------------------------------------------------------------------

const address = z.email("Enter a valid email").trim().toLowerCase();

/** What "Send" posts to /api/po/:id/email. The id is made when the dialog opens, so a retry sends once. */
export const sendEmailArgs = z.object({
  id: z.uuid(),
  account_id: z.uuid("Choose who it's from"),
  to: z.array(address).min(1, "Add at least one recipient").max(20),
  cc: z.array(address).max(20).default([]),
  subject: z
    .string()
    .trim()
    .min(1, "Add a subject")
    .max(300)
    .refine((s) => !/[\r\n]/.test(s), "The subject must be one line"),
  body: z.string().trim().min(1, "Write a message").max(20_000),
  /** poStamp() of the PO as the dialog showed it: if it has changed since, the server asks to look again. */
  stamp: z.string().min(1).max(40),
  /** The revision the dialog was opened for (shown in messages). */
  revision: z.number().int().min(0).optional(),
  /** The sender's time zone, for the date on the PDF. */
  tz: z.string().max(100).optional(),
  /** Check against the plan --- sent although the check showed differences: kept on the PO when this email marks it sent. */
  plan_check: planCheckSeen.optional(),
});
export type SendEmailArgs = z.infer<typeof sendEmailArgs>;
